import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2 } from "lucide-react";
import stoneHeroImage from "@/assets/stone-masonry-installation-oakland-nj.webp";
import stoneMasonryTotowaImage from "@/assets/stone-masonry-totowa-passaic-nj.webp";
import stoneVeneerPassaicImage from "@/assets/stone-veneer-passaic-nj.webp";
import stoneVeneerFairviewImage from "@/assets/stone-veneer-installation-fairview-nj.webp";
import stoneVeneerParamus061826Asset from "@/assets/mp-stone-veneer-installation-paramus-nj-061826.webp";

const faqs = [
  {
    question: "Do I need a permit for stone veneer installation in North Jersey?",
    answer: "Most exterior cladding changes require a building permit. Requirements vary by municipality across Bergen, Passaic, Morris, and Essex counties. Your contractor should confirm with the local building department before any work begins. Permits protect you by making sure the installation meets current building codes for your area.",
  },
  {
    question: "What type of mortar do North Jersey masons use for exterior stone veneer?",
    answer: "Type S mortar is the standard for exterior stone veneer in North Jersey because of its high bond strength and resistance to freeze-thaw cycles. Type N mortar works well for interior projects like fireplaces and accent walls, where flexibility is more important than raw bonding power. Your mason will match the mortar type to the specific application.",
  },
  {
    question: "Can stone veneer be installed over my existing siding in North Jersey?",
    answer: "Existing siding is typically removed first. Stone veneer needs a stable substrate like concrete block, plywood, or OSB sheathing to support its weight properly. Your mason will assess the wall condition and prep it to meet ASTM C1780 standards before any stone goes on.",
  },
  {
    question: "How long does a stone veneer installation take on a North Jersey home?",
    answer: "Most residential facade projects take 3 to 7 days, depending on wall size, substrate condition, and weather. Interior fireplaces often finish in 1 to 3 days. Cold-weather installations may add a day for setup of heated enclosures and protective blankets.",
  },
  {
    question: "Can stone veneer be installed in winter in North Jersey?",
    answer: "Yes, with cold-weather provisions in place. Our crews protect fresh mortar from freezing by using heated enclosures and insulated blankets when temperatures fall below 40°F. Winter installations are common across Bergen, Morris, and Passaic counties with proper precautions.",
  },
  {
    question: "Should I hire a masonry contractor or a general handyman for stone veneer?",
    answer: "An insured masonry contractor with stone veneer experience provides the safest and longest-lasting result. Stone veneer installation requires knowledge of mortar types, weather barriers, metal lath systems, and local building codes. A general handyman may not have the specialized training needed for a proper adhered veneer system.",
  },
];

const citations = [
  {
    id: 1,
    text: "Mason Contractors Association of America (MCAA) — Stone Veneer Glossary — National Trade Association",
    url: "https://www.masoncontractors.org/",
  },
  {
    id: 2,
    text: "ASTM C1780-20 — Standard Practice for Installation Methods for Cement-based Adhered Masonry Veneer — International Standards Organization",
    url: "https://www.astm.org/c1780-20.html",
  },
  {
    id: 3,
    text: "Concrete Masonry and Hardscapes Association (CMHA) — ASTM Standards for Manufactured Stone Veneer — National Industry Association",
    url: "https://www.masonryandhardscapes.org/",
  },
  {
    id: 4,
    text: "Penn State Pennsylvania Housing Research Center (PHRC) — Manufactured Stone Veneer: Sticking to the Basics — University Research Center",
    url: "https://www.phrc.psu.edu/",
  },
  {
    id: 5,
    text: "Construction Specifier — Association Updates Guidelines for Adhered Manufactured Stone Veneer — Industry Publication (CSI)",
    url: "https://www.constructionspecifier.com/",
  },
];

const installationSteps = [
  "Inspect the wall substrate and confirm it can support the veneer weight.",
  "Install two layers of weather-resistive barrier over wood sheathing.",
  "Fasten corrosion-resistant metal lath to wall studs.",
  "Apply a half-inch scratch coat of Type S or Type N mortar and let it cure.",
  "Dampen the back of each stone, then butter it with a half-inch mortar bed.",
  "Press each stone firmly into the scratch coat, working from the bottom up.",
  "Tool mortar joints once thumbprint-hard, then clean residue with water.",
];

const CitationLink = ({ id, url }: { id: number; url: string }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="text-secondary hover:text-secondary/80 transition-colors"
  >
    <sup className="text-xs">[{id}]</sup>
  </a>
);

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

export default function StoneVeneer() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Stone Veneer Installation Totowa, NJ | Masonry Pro</title>
        <meta property="og:description" content="Thinking about stone veneer for your home? We install facades, fireplaces, and accent walls across North Jersey. Insured masons, year-round scheduling." />
        <meta property="og:title" content="Stone Veneer Installation Totowa, NJ | Masonry Pro" />
        <meta
          name="description"
          content="Thinking about stone veneer for your home? We install facades, fireplaces, and accent walls across North Jersey. Insured masons, year-round scheduling."
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Stone Veneer Installation",
            description: "Stone veneer installation for facades, fireplaces, and accent walls across North Jersey.",
            url: "/masonry-services/stone-veneer",
            image: "https://masonry-pro.com/og/stone-veneer.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/stone-veneer.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/stone-veneer.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={stoneHeroImage} alt="Stone veneer installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Stone Veneer Installation in Totowa, NJ Serving <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '1rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Walls, Fireplaces, and Facades That Last
                </p>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.125rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  In North Jersey, stone veneer adds lasting curb appeal to homes and businesses across <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties. We install stone veneer on exterior facades, fireplaces, accent walls, and foundation covers. Our crews work year-round, with cold-weather provisions for winter installs. We're insured masonry contractors who follow ASTM C1780 installation standards on every project.
                  <CitationLink id={2} url="https://www.astm.org/c1780-20.html" />
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="/contact">Get a Free Quote</a>
                </Button>
              </div>
            </div>
          </section>

          <PackageCallout packageId="curb-appeal" packageName="Curb Appeal Package" blurb={"Pair stone veneer with new masonry steps, a stoop, and a paver walkway as a single front-entry package. Matched details, one crew, one timeline."} />

          {/* Section 1: Mortar Types */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    The Right Mortar Type Makes Stone Veneer Stick for Decades
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      There are a few different types of mortar for installing stone veneer, and picking the wrong type can cost you thousands in repairs down the road. Veneer is one of the most requested parts of the <Link to="/" className="text-secondary hover:underline">masonry work we do across North Jersey</Link>.
                    </p>
                    <p>
                      Type S mortar is used on most exterior stone veneer projects. It has higher bond strength, meaning the stone remains secure through wind, rain, and temperature fluctuations. For interior work like accent walls and fireplaces, Type N mortar is usually the better choice. It's more flexible, which matters when heat and settling are factors.
                    </p>
                    <p>
                      Both mortar types are defined under ASTM C270 by proportion or property specifications. Premixed mortars that meet ASTM C1714 also help reduce mixing errors on the job site. Consistent mortar means a consistent bond.
                    </p>
                    <p>
                      North Jersey gets hot, humid summers and sub-freezing winters. That kind of temperature swing puts serious stress on any exterior surface. It's why Type S mortar is the best for outdoor stone veneer installations across Bergen, Passaic, and Morris counties. A masonry contractor who knows the area matches mortar type to the project location and the wall underneath it. Homeowners in Ridgewood, Montclair, and Wayne ask about this a lot, and it's a fair question. The mortar behind the stone matters just as much as the stone itself. Watch a <Link to="/videos/stone-veneer-wayne-nj" className="text-secondary hover:underline">stone veneer front entry we built in Wayne</Link>.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={stoneMasonryTotowaImage}
                    alt="Variety of stone masonry patterns, including stacked stone, brick, and river rock in Totowa, Passaic County, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Wall Prep — full width */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">
                  Your Wall Needs Proper Prep Before Any Stone Goes On
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    Understanding what lies behind the stone is more important than many realize. Skipping proper preparation can lead to moisture damage or the veneer detaching within a few years.
                  </p>
                  <p>
                    Stone veneer can be anchored to concrete block, poured concrete, or wood-framed walls with sheathing, but drywall alone cannot support its weight without additional structural support or mechanical fasteners.
                  </p>
                  <p>
                    Wood-framed walls need two layers of weather-resistant barrier before applying stone, as specified by ASTM C1780.
                    <CitationLink id={2} url="https://www.astm.org/c1780-20.html" /> If the surface is painted, sealed, or damaged, it must be cleaned off first, since new mortar won't adhere to a compromised surface.
                  </p>
                  <p>
                    A masonry contractor in North Jersey should also verify local building codes before starting, as permit requirements vary by municipality; what's permitted in Clifton may not be permitted in Morristown or Wyckoff.
                  </p>
                  <p>
                    It's common in Bergen County to find older colonials and split-levels with a mix of original block and newer sheathing on the same wall, requiring two preparation methods for one project. Although it adds a step, proper initial prep prevents future problems. The contractor should recognize this during the initial walkthrough, before the lath is installed.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Mortar vs Adhesive — with image */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={stoneVeneerFairviewImage}
                    alt="Stone veneer installation on a residential home exterior in Fairview, Bergen County, New Jersey"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1024}
                    height={1024}
                  />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Mortar Outperforms Glue and Adhesive on Exterior Stone Veneer
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      A common question is whether construction adhesive or thinset can replace mortar for stone veneer. The quick answer is no, at least not on exterior walls.
                    </p>
                    <p>
                      Construction adhesives such as Liquid Nails lack the compressive strength required for outdoor veneer. They might hold lightweight materials indoors, but outside, stone faces wind loads, moisture, and seasonal movement, things that adhesives simply weren't designed to handle. Thinset works well for interior tile, but it's not rated for the thicker stone veneer on exterior surfaces. Grout fills in the joints between stones, but it doesn't bond the stones to the wall.
                    </p>
                    <p>
                      According to ASTM C1780, mortar is the required setting material for adhered stone veneer systems.
                      <CitationLink id={2} url="https://www.astm.org/c1780-20.html" /> In North Jersey, professional installers typically use Type S or Type N mortar, depending on the project. There are no exceptions for using adhesives or glues on the outside.
                    </p>
                    <p>
                      Homes near the Passaic River and in low-lying parts of Essex County face higher humidity levels than those on higher ground. That extra moisture makes a strong mortar bond even more important. Adhesive products can soften or lose grip over time in persistent humidity, while mortar cures into a solid, load-bearing bond that stays stable despite moisture exposure. Some homeowners in Paramus and Nutley ask about shortcuts they see online, but there is a professional way for a reason.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Scratch Coat and Metal Lath */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A Scratch Coat and Metal Lath Lock Stone Veneer to the Wall
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      The scratch coat and lath system provides the stone veneer with its grip. Without it, you're relying on mortar alone to hold weight against a smooth surface. That doesn't work for long.
                    </p>
                    <p>
                      Metal lath gets fastened horizontally to the wall studs using corrosion-resistant fasteners, following ASTM C1063 standards. The lath gives mortar something to grab onto. Over that, a half-inch scratch coat of mortar is applied and scored with grooves while it's still wet. Those grooves create texture so the stone has a rough surface to press into.
                    </p>
                    <p>
                      The scratch coat needs time to cure before any stone goes on. Rushing this step leads to shifting and cracking after the veneer is already set. Once the scratch coat is ready, each stone gets pressed firmly into a fresh mortar bed, working from the bottom of the wall upward.
                    </p>
                    <p>
                      Before the scratch coat goes on, our crews inspect the lath overlap and fastener spacing. If the lath isn't secured properly or the overlaps are too short, the entire system is weaker than it should be. Property owners in Glen Rock, Caldwell, and Parsippany hiring a contractor for a first-time veneer project should expect that inspection as a standard part of the process.
                    </p>
                    <p>
                      Bergen County's coastal-influenced weather accelerates fastener corrosion more than most people expect. Galvanized or stainless steel lath and fasteners are the smart default in this area. Regular steel starts rusting behind the wall where you can't see it, and by the time the problem shows on the surface, the damage is already done.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={stoneHeroImage}
                    alt="Stone masonry installation in Oakland, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Bond Problems */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div className="order-2 lg:order-1 rounded-xl overflow-hidden border border-border">
                  <img
                    src={stoneVeneerParamus061826Asset}
                    alt="Home entrance with stone veneer installation and steps in Paramus, Bergen County, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Mortar That Won't Bond Usually Points to a Surface or Mix Problem
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      Is mortar falling off a stone after it's been set? Usually, it all boils down to surface prep or the mix itself. Dusty walls, painted surfaces, and old sealant can stop the mortar from sticking properly. It might look fine at first, but give it a few weeks, and pieces might start to come loose. Don't forget the back of the stone; if it hasn't been cleaned or scuffed, the mortar has nothing to hold onto.
                    </p>
                    <p>
                      Here's a tricky one many folks overlook: dry stone actually pulls moisture out of the mortar before it can fully cure. The solution is simple: just lightly dampen the back of each piece before setting it. You're not soaking it, just enough to keep the stone from stealing moisture from the mortar mix.
                    </p>
                    <p>
                      On the flip side, too much water in the mortar weakens it, and an off-balance mix can do the same. That's why it's always a good idea to check the mortar's consistency before working on each section. It's easier to make adjustments now than to deal with bond failures down the line.
                    </p>
                    <p>
                      Summer in North Jersey brings its own challenges. The mortar can dry out quickly on hot days, especially on south-facing walls in the afternoon. Mason crews in Bloomfield, Hackensack, and Boonton know to mist their work areas and plan their pours to avoid the direct sun. It might seem like a small detail, but it can be the difference between a bond that lasts and one that fails.
                      <CitationLink id={1} url="https://www.masoncontractors.org/" />
                      <CitationLink id={4} url="https://www.phrc.psu.edu/" />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: Freeze-Thaw */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    North Jersey Freeze-Thaw Cycles Demand Weatherproof Stone Veneer Details
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      Water behind the stone veneer is where the real problems begin. Moisture sneaks in through a gap or a poorly sealed joint, then sits behind the stone and freezes. When water freezes, it expands, pushing the stone right off the wall. This process happens slowly, but once it starts, the damage can spread quickly.
                    </p>
                    <p>
                      In North Jersey, we experience over 25 freeze-thaw cycles each winter
                      <CitationLink id={1} url="https://www.masoncontractors.org/" />, which means more opportunities for trapped moisture to cause trouble between November and March. Both the MCAA and ASTM recommend protective measures for adhered veneer in climates like ours.
                    </p>
                    <p>
                      Installing two layers of weather-resistant barrier behind the veneer, along with integrated flashing at windows and doors, helps keep water out of the wall assembly. At the bottom of the wall, a weep screed allows any trapped moisture to drain away instead of lingering behind the stone. Without it, water has nowhere to go.
                    </p>
                    <p>
                      The profile of the mortar joints also matters: concave or V-groove joints shed water effectively, while flat-topped joints tend to hold water along the edges and provide an inward path. This detail requires no extra time but makes a significant difference over the life of the installation.
                    </p>
                    <p>
                      When temperatures drop below 40°F, our crews take cold-weather precautions on exterior work, using heated enclosures and insulated blankets to prevent new mortar from freezing before it cures. Homeowners in Morris and Passaic counties plan their projects year-round, confident that these precautions are in place. There's no need to wait until spring if the contractor knows how to handle the conditions.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={stoneVeneerPassaicImage}
                    alt="Stone veneer installed on a home exterior in Passaic, Passaic County, NJ by Masonry Pro"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Featured Snippet Section */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 border border-secondary/20 rounded-2xl p-8 md:p-12">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                    What Is the Best Way to Install Stone Veneer in North Jersey?
                  </h2>
                  <ol className="space-y-4">
                    {installationSteps.map((step, index) => (
                      <li key={index} className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center">
                          <CheckCircle2 className="h-5 w-5 text-secondary" />
                        </div>
                        <span className="text-muted-foreground pt-1">{step}</span>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-6 text-sm text-muted-foreground text-center">
                    Masonry contractors in North Jersey follow ASTM C1780 standards
                    <CitationLink id={2} url="https://www.astm.org/c1780-20.html" /> and add cold-weather provisions when temperatures fall below 40°F.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* YouTube Section */}
          <YouTubeShortsSection
            title="See Our Stone Veneer Work in Action"
            videoIds={["hMO7o0DWqAw", "0Kid2ea7tik", "c_50pnhg30w"]}
          />

          {/* FAQ Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border hover:border-secondary/50 transition-colors"
                  >
                    <h3 className="text-lg font-bold mb-3 text-foreground">
                      {faq.question}
                    </h3>
                    <p className="text-sm text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" />

          {/* CTA Section */}
          <CTASection
            heading="Ready to Add Stone Veneer to Your"
            highlightedHeading="North Jersey Home?"
            serviceName="Stone Veneer Installation"
          />

          {/* Citations Section */}
          <section className="py-8 bg-background">
            <div className="container mx-auto px-4">
              <Accordion type="single" collapsible className="max-w-4xl mx-auto">
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:text-foreground">
                    Citations & References
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="space-y-2 text-sm text-muted-foreground">
                      {citations.map((citation) => (
                        <li key={citation.id} className="flex gap-2">
                          <span className="text-secondary">[{citation.id}]</span>
                          <a
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-secondary transition-colors"
                          >
                            {citation.text}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
